import { styled } from '@mui/material';

import { Navigate, useParams } from 'react-router';

import { useMemo } from 'react';

import { Typography, PageContent } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';

import { useReportContext } from '../../context/useReportContext';

import { SinglePath } from '../../types';
import { PathsCategory } from './components/PathsCategory';
import { ReportPaths } from '../../paths/ReportPaths';

const ContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
}));

const groupSimulatedPathsByPersona = (paths: SinglePath[]) => {
  const simulatedPaths = paths.filter((path) => path.type === 'simulated');
  const personaKeys = [
    ...new Set(simulatedPaths.map((path) => path.persona || 'default')),
  ];

  return personaKeys.reduce<
    Record<string, { successful: SinglePath[]; unsuccessful: SinglePath[] }>
  >((grouped, personaKey) => {
    grouped[personaKey] = {
      successful: simulatedPaths.filter(
        (path) =>
          path.status === 'COMPLETED' &&
          (path.persona || 'default') === personaKey
      ),
      unsuccessful: simulatedPaths.filter(
        (path) =>
          path.status === 'CANCELED' &&
          (path.persona || 'default') === personaKey
      ),
    };

    return grouped;
  }, {});
};

export const Paths = () => {
  const { t } = useI18n();
  const { type } = useParams<{
    type: 'human' | 'simulated';
  }>();
  const { paths, baseURL } = useReportContext();

  const groupedPaths: null | {
    [index: string]:
      | {
          unsuccessful: SinglePath[];
          successful: SinglePath[];
        }
      | undefined;
  } = useMemo(() => {
    switch (type) {
      case 'human': {
        const successfulPaths = paths.filter(
          (path) => path.status === 'COMPLETED' && path.type === type
        );

        const unsuccessfulPaths = paths.filter(
          (path) => path.status === 'CANCELED' && path.type === type
        );

        return {
          default: {
            successful: successfulPaths,
            unsuccessful: unsuccessfulPaths,
          },
        };
      }

      case 'simulated':
        return groupSimulatedPathsByPersona(paths);

      default:
        return null;
    }
  }, [paths, type]);

  if (!type || !['human', 'simulated'].includes(type)) {
    return <Navigate to={ReportPaths.goToReportOverview(baseURL)} />;
  }

  if (!groupedPaths) return null;

  return (
    <PageContent>
      <ContentWrapper>
        <header>
          <PageContent.Title title={t('prototypes:report.paths.title')} />
          <Typography>
            {t(`prototypes:report.paths.${type}.description`)}
          </Typography>
        </header>
        {Object.keys(groupedPaths).map((category) => (
          <PathsCategory
            key={category}
            categoryName={category}
            category={groupedPaths[category]}
          />
        ))}
      </ContentWrapper>
    </PageContent>
  );
};
